<template>
  <div class="min-h-screen bg-base-200 p-4 flex items-center justify-center">
    <div class="w-full max-w-md mx-auto bg-base-100 rounded-lg shadow-xl">
      <!-- Header with Logo -->
      <div class="bg-primary text-primary-content p-6 rounded-t-lg text-center space-y-3">
        <div class="flex justify-center">
          <div class="w-16 h-16 bg-primary-content/20 rounded-full flex items-center justify-center">
            <span class="text-2xl font-bold">LRS</span>
          </div>
        </div>
        <h2 class="text-2xl font-bold">Buy LRS Coins</h2>
        <div class="text-sm opacity-90">
          <p>Current Rate</p>
          <div class="flex flex-col items-center gap-1">
            <div class="flex items-center justify-center gap-2">
              <p class="text-xl font-semibold">1 LRS = ₹{{ ourPrice.toFixed(2) }}</p>
              <button
                class="btn btn-circle btn-xs btn-ghost"
                @click="refreshPrice"
                :class="{ 'loading': isLoading }"
              >
                ↻
              </button>
            </div>
          </div>
          <p class="text-xs opacity-75 mt-1">Last updated: {{ lastUpdated }}</p>
        </div>
      </div>

      <!-- Purchase Form -->
      <div class="p-4 sm:p-6 space-y-5">
        <!-- Amount Input -->
        <div class="form-control w-full">
          <label class="label">
            <span class="label-text font-medium">Amount (INR)</span>
          </label>
          <label class="input input-bordered flex items-center gap-2 h-14">
            <span class="text-lg font-semibold text-black">₹</span>
            <input
              type="number"
              v-model="amount"
              class="grow text-lg text-black"
              placeholder="0.00"
              min="0"
            />
          </label>
          <label class="label">
            <span class="label-text-alt">You will receive:</span>
            <span class="label-text-alt font-semibold text-primary">{{ lrsAmount }} LRS</span>
          </label>
        </div>

        <!-- Wallet Address -->
        <div class="form-control w-full">
          <label class="label">
            <span class="label-text font-medium">Larissa Wallet Address</span>
          </label>
          <input
            type="text"
            v-model="walletAddress"
            class="input input-bordered w-full h-14"
            :class="{ 'input-error': walletError }"
            placeholder="Enter your Larissa wallet address (0x...)"
          />
          <label class="label" v-if="walletError">
            <span class="label-text-alt text-error">{{ walletError }}</span>
          </label>
          <label class="label" v-if="isCheckingWallet">
            <span class="label-text-alt">
              <span class="loading loading-spinner loading-xs"></span>
              Validating wallet...
            </span>
          </label>
        </div>

        <!-- Buy Button -->
        <button
          @click="showPaymentQR"
          class="btn btn-primary w-full h-14 text-lg"
          :disabled="!isValidForm"
        >
          <span v-if="isCheckingWallet">
            <span class="loading loading-spinner loading-sm mr-2"></span>
            Validating Wallet...
          </span>
          <span v-else>
            Buy LRS Coins
          </span>
        </button>
      </div>

      <!-- Payment Modal -->
      <dialog class="modal modal-bottom sm:modal-middle" :class="{ 'modal-open': showModal }">
        <div class="modal-box relative bg-white text-black">
          <button
            class="btn btn-sm btn-circle absolute right-2 top-2"
            @click="closeModal"
          >✕</button>

          <!-- Payment QR and Details -->
          <div v-if="!showPaymentVerification">
            <h3 class="font-bold text-lg text-center mb-4 text-black">Scan & Pay</h3>
            <div class="space-y-6">
              <!-- Add countdown timer -->
              <div class="text-center text-sm text-black opacity-70">
                Refreshing in {{ countdown }}s
              </div>

              <!-- QR Code -->
              <div class="flex justify-center">
                <div class="bg-white p-4 rounded-lg">
                  <QRCodeVue
                    :value="upiPaymentLink"
                    :size="256"
                    level="H"
                    render-as="svg"
                    :margin="1"
                    class="mx-auto"
                  />
                </div>
              </div>
              <div class="text-center space-y-3">
                <div class="flex flex-col items-center gap-2">
                  <span class="text-sm text-black opacity-70">UPI ID</span>
                  <div class="badge bg-gray-100 text-black p-3 select-all">
                    {{ paymentConfig.upiId }}
                  </div>
                </div>
                <div class="divider text-black">Amount to Pay</div>
                <div class="space-y-1">
                  <p class="text-2xl font-bold text-black">₹{{ amount }}</p>
                  <p class="text-sm text-black">For {{ lrsAmount }} LRS</p>
                </div>
                <div class="text-sm text-black space-y-2">
                  <p>Please complete the payment using any UPI app</p>
                  <p class="text-xs opacity-70">
                    Scan QR code or copy UPI ID to pay
                  </p>
                </div>
              </div>
              <div class="space-y-3 pt-2">
                <button
                  class="btn btn-success w-full"
                  @click="confirmPayment"
                >
                  I have completed the payment
                </button>
                <button
                  class="btn btn-ghost w-full"
                  @click="closeModal"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>

          <!-- Payment Verification Form -->
          <div v-else>
            <h3 class="font-bold text-lg text-center mb-4 text-black">Verify Payment</h3>
            <div class="space-y-4">
              <!-- Transaction ID Input -->
              <div class="form-control">
                <label class="label">
                  <span class="label-text text-black">UPI Transaction ID</span>
                </label>
                <input
                  type="text"
                  v-model="transactionId"
                  class="input input-bordered text-black"
                  placeholder="Enter your UPI transaction ID"
                />
              </div>

              <!-- OR Divider -->
              <div class="divider text-black opacity-70">OR</div>

              <!-- Screenshot Upload -->
              <div class="form-control">
                <label class="label">
                  <span class="label-text text-black">Upload Payment Screenshot</span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  @change="handleFileUpload"
                  class="file-input file-input-bordered w-full text-black"
                />
                <label class="label">
                  <span class="label-text-alt text-black opacity-70">Max size: 5MB</span>
                </label>
              </div>

              <!-- Submit Buttons -->
              <div class="space-y-3 pt-4">
                <button
                  class="btn btn-primary w-full"
                  @click="submitPaymentVerification"
                  :disabled="isSubmittingPayment || (!transactionId && !paymentScreenshot)"
                >
                  <span v-if="isSubmittingPayment">
                    <span class="loading loading-spinner loading-sm mr-2"></span>
                    Submitting...
                  </span>
                  <span v-else>
                    Submit Verification
                  </span>
                </button>
                <button
                  class="btn btn-ghost w-full"
                  @click="showPaymentVerification = false"
                  :disabled="isSubmittingPayment"
                >
                  Back to Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      </dialog>

      <!-- Add this after successful payment verification -->
      <dialog class="modal" :class="{ 'modal-open': showSuccessModal }">
        <div class="modal-box bg-white text-black">
          <h3 class="font-bold text-lg text-center mb-4">Payment Submitted Successfully!</h3>
          <div class="space-y-4">
            <div class="text-center">
              <p class="text-sm mb-2">Your Transaction ID</p>
              <div class="flex items-center justify-center gap-2">
                <div class="badge badge-primary badge-lg font-mono text-lg p-4">
                  {{ currentTransactionId }}
                </div>
                <button
                  class="btn btn-ghost btn-sm"
                  @click="copyTransactionId"
                  :class="{ 'btn-success': copied }"
                >
                  <span v-if="copied">Copied!</span>
                  <span v-else>Copy</span>
                </button>
              </div>
            </div>
            <div class="space-y-2 text-center">
              <p class="text-sm opacity-70">
                Please save this ID for future reference
              </p>
              <p class="text-sm text-purple-600">
                Your payment will be verified manually and LRS coins will be transferred to your wallet shortly.
                This process may take some time.
              </p>
            </div>
            <button
              class="btn btn-primary w-full"
              @click="closeSuccessModal"
            >
              Done
            </button>
          </div>
        </div>
      </dialog>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { getLRSPrice, verifyLRSCoin } from '../services/coingecko'
import { ethers } from 'ethers'
import QRCodeVue from 'qrcode.vue'
import { paymentConfig } from '../config/payment'

// Constants
const LARISSA_RPC = 'https://rpc.larissa.network'
const LARISSA_CHAIN_ID = 9898

// Setup provider
const provider = new ethers.JsonRpcProvider(LARISSA_RPC, {
  chainId: LARISSA_CHAIN_ID,
  name: 'Larissa Network'
})

const amount = ref('')
const walletAddress = ref('')
const showModal = ref(false)
const coingeckoPrice = ref(1.66)
const ourPrice = ref(1.83)
const isLoading = ref(false)
const lastUpdateTime = ref(new Date())
const isCheckingWallet = ref(false)
const walletError = ref('')
const isWalletValid = ref(false)

// Add new refs for payment verification
const showPaymentVerification = ref(false)
const transactionId = ref('')
const paymentScreenshot = ref<File | null>(null)
const isSubmittingPayment = ref(false)

// Add these refs
const showSuccessModal = ref(false)
const currentTransactionId = ref('')
const copied = ref(false)

// Add new ref for payment modal refresh interval
let paymentRefreshInterval: number | null = null

// Add new ref for countdown
const countdown = ref(30)

// Computed Properties
const lrsAmount = computed(() => {
  const numAmount = Number(amount.value)
  if (!numAmount || numAmount <= 0) return '0.0000'

  // Calculate LRS amount using our price (with markup)
  // Use precise division and round to 4 decimal places
  const lrs = Math.round((numAmount / ourPrice.value) * 10000) / 10000
  return lrs.toFixed(4)
})

const lastUpdated = computed(() => {
  return new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
    .format(Math.round((lastUpdateTime.value.getTime() - new Date().getTime()) / 60000), 'minute')
})

const isValidWallet = computed(() => {
  if (!walletAddress.value) return true // Allow empty input

  try {
    // Simple hex address validation
    if (!/^0x[a-fA-F0-9]{40}$/.test(walletAddress.value)) {
      walletError.value = 'Invalid wallet address format'
      return false
    }

    return true
  } catch {
    walletError.value = 'Invalid wallet address'
    return false
  }
})

const isValidForm = computed(() => {
  const numAmount = Number(amount.value)
  return numAmount > 0 &&
         isValidWallet.value &&
         walletAddress.value &&
         isWalletValid.value &&
         !isCheckingWallet.value &&
         !walletError.value
})

// Computed property for UPI payment link
const upiPaymentLink = computed(() => {
  const amountValue = Number(amount.value)
  if (!amountValue) return ''

  // Create UPI payment link
  const upiLink = `upi://pay?pa=${paymentConfig.upiId}&pn=${encodeURIComponent(paymentConfig.merchantName)}&am=${amountValue}&cu=INR`
  return upiLink
})

// Methods
async function refreshPrice() {
  if (isLoading.value) return

  isLoading.value = true
  try {
    const prices = await getLRSPrice()
    coingeckoPrice.value = prices.coingeckoPrice
    ourPrice.value = prices.ourPrice
    lastUpdateTime.value = new Date()
  } catch (error) {
    console.error('Failed to refresh price:', error)
    alert('Unable to fetch current price. Using default price.')
  } finally {
    isLoading.value = false
  }
}

// Auto refresh price every 5 minutes
let refreshInterval: number

onMounted(async () => {
  // Verify coin info first
  await verifyLRSCoin()
  await refreshPrice()
  refreshInterval = setInterval(refreshPrice, 5 * 60 * 1000)
})

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
  }
  stopPaymentRefresh()
})

// Update startPaymentRefresh method
const startPaymentRefresh = () => {
  // Initial refresh
  refreshPrice()
  countdown.value = 30

  // Set up 30-second interval
  paymentRefreshInterval = setInterval(() => {
    countdown.value -= 1
    if (countdown.value <= 0) {
      countdown.value = 30
      refreshPrice()
    }
  }, 1000) // Update countdown every second
}

// Update stopPaymentRefresh method
const stopPaymentRefresh = () => {
  if (paymentRefreshInterval) {
    clearInterval(paymentRefreshInterval)
    paymentRefreshInterval = null
    countdown.value = 30
  }
}

// Update showPaymentQR method
const showPaymentQR = () => {
  showModal.value = true
  startPaymentRefresh()
}

// Update confirmPayment method
const confirmPayment = () => {
  stopPaymentRefresh()
  showPaymentVerification.value = true
}

// Update closeModal method
const closeModal = () => {
  stopPaymentRefresh()
  showModal.value = false
  resetPaymentForm()
}

const resetPaymentForm = () => {
  transactionId.value = ''
  paymentScreenshot.value = null
  showPaymentVerification.value = false
}

const handleFileUpload = (event: Event) => {
  const input = event.target as HTMLInputElement
  if (input.files && input.files[0]) {
    const file = input.files[0]
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size should not exceed 5MB')
      input.value = ''
      return
    }
    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file')
      input.value = ''
      return
    }
    paymentScreenshot.value = file
  }
}

// Update the wallet validation function
const validateWalletOnChain = async (address: string) => {
  isCheckingWallet.value = true
  walletError.value = ''
  isWalletValid.value = false  // Reset validation state

  try {
    // Simple hex address validation (0x followed by 40 hex characters)
    if (!/^0x[a-fA-F0-9]{40}$/.test(address)) {
      walletError.value = 'Invalid wallet address format'
      return false
    }

    // Get the code at the address
    const code = await provider.getCode(address)

    // Check if address has code (is a contract)
    if (code !== '0x') {
      walletError.value = 'Contract addresses are not allowed'
      return false
    }

    isWalletValid.value = true  // Set validation state to true if all checks pass
    return true
  } catch (error) {
    console.error('Error validating wallet:', error)
    walletError.value = 'Unable to verify wallet on Larissa Network'
    return false
  } finally {
    isCheckingWallet.value = false
  }
}

// Update the wallet watcher to reset validation state
watch(walletAddress, async (newAddress) => {
  isWalletValid.value = false  // Reset validation state when address changes
  if (!newAddress) {
    walletError.value = ''
    return
  }

  // Simple format validation before chain check
  if (/^0x[a-fA-F0-9]{40}$/.test(newAddress)) {
    await validateWalletOnChain(newAddress)
  }
}, { debounce: 500 })

// Add new method for payment verification
const submitPaymentVerification = async () => {
  try {
    if (!transactionId.value && !paymentScreenshot.value) {
      alert('Please provide either Transaction ID or Payment Screenshot')
      return
    }

    isSubmittingPayment.value = true
    const formData = new FormData()
    formData.append('amount', amount.value)
    formData.append('lrsAmount', lrsAmount.value)
    formData.append('walletAddress', walletAddress.value)

    if (transactionId.value) {
      formData.append('upiTransactionId', transactionId.value)
    }

    if (paymentScreenshot.value) {
      formData.append('screenshot', paymentScreenshot.value)
    }

    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/verify-payment`, {
      method: 'POST',
      body: formData
    })

    const data = await response.json()

    if (data.success) {
      currentTransactionId.value = data.transactionId
      showSuccessModal.value = true
      resetPaymentForm()
    } else {
      throw new Error(data.message)
    }
  } catch (error) {
    console.error('Payment verification error:', error)
    alert(error.message || 'Failed to verify payment. Please try again.')
  } finally {
    isSubmittingPayment.value = false
  }
}

const copyTransactionId = async () => {
  try {
    await navigator.clipboard.writeText(currentTransactionId.value)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

const closeSuccessModal = () => {
  showSuccessModal.value = false
  currentTransactionId.value = ''
  closeModal()
}
</script>

<style scoped>
/* Remove number input spinners */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type="number"] {
  -moz-appearance: textfield;
}

/* Remove input background in Chrome autocomplete */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0px 1000px white inset;
}

/* Add black text color for input */
input {
  color: black !important;
}

/* Ensure placeholder text is slightly lighter */
input::placeholder {
  color: #6b7280 !important;
}

/* Add styles for QR code container */
.qr-container {
  background: white;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1);
}

/* Add styles for file input */
.file-input::file-selector-button {
  color: black;
}

/* Add styles for modal content */
.modal-box {
  color: black !important;
  background-color: white !important;
}

/* Style for input placeholders */
.input::placeholder {
  color: #6b7280 !important;
  opacity: 0.7;
}

/* Style for file input */
.file-input {
  color: black !important;
}

.file-input::file-selector-button {
  color: black !important;
}

/* Style for divider */
.divider {
  color: black !important;
  opacity: 0.7;
}

/* Style for labels */
.label-text, .label-text-alt {
  color: black !important;
}
</style>